import React from 'react';

import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconTags } from '@tabler/icons-react';
import useUpdateAppointmentCoachRole from 'hooks/appointmentCoachRole/useUpdateAppointmentCoachRole';
import { AppointmentCoachRole } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalEditAppointmentCoachRoleProps {
  opened: boolean;
  handleClose: () => void;
  appointmentCoachRole: AppointmentCoachRole;
}

interface FormValues {
  name: string;
  appointmentCoachRoleId: string;
}

const ModalEditAppointmentCoachRole = ({
  opened,
  handleClose,
  appointmentCoachRole,
}: IModalEditAppointmentCoachRoleProps): JSX.Element => {
  const updateMutation = useUpdateAppointmentCoachRole();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      appointmentCoachRoleId: '',
    },

    validate: {
      name: value =>
        value.length < 3 || value.length > 120 ? 'Name must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleSubmit = (values: FormValues): void => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        successNotification(
          'Updating coach role for appointment success',
          `${values.name} role has been successfully updated.`,
        );
        handleCloseAndReset();
      },
      onError: error => {
        errorNotification('Update coach role for appointment failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    form.setValues(appointmentCoachRole);
  }, [appointmentCoachRole]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Edit Coach Role For Appointments</Title>}
      size='auto'
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Role Name'
            data-cy='editAppointmentCoachRoleTextInput'
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={updateMutation.isLoading}>
              Cancel
            </Button>
            <Button
              disabled={updateMutation.isLoading}
              type='submit'
              data-cy='saveEditAppointmentCoachRoleButton'
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalEditAppointmentCoachRole;
